import { useTranslation } from 'react-i18next';

import CirclePlay from '@/assets/icons/circle_play.svg?component';
import Reload from '@/assets/icons/reload.svg?component';
import VideoLoadingSpinner from '@/assets/icons/video_loading_spinner.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import settings from '@/config/settings';

import type { ControlState } from '../../types/videoStream';

import { StopConnectionButton } from './StopConnectionButton';

import styles from './BehaviorVideo.module.scss';

interface PlaceholderViewProps {
    cageName?: string;
    controlState: ControlState;
    onStart: () => void;
}

export function PlaceholderView(props: PlaceholderViewProps) {
    const { cageName, controlState, onStart } = props;
    const { t } = useTranslation('behaviorModule');

    const isDisabled = controlState.isButtonDisabled;

    const buttonTitle = controlState.isOutOfQuota
        ? t('videoStream.quotaExceeded', 'Daily streaming quota exceeded')
        : !controlState.isOnline
          ? t('videoStream.bioscopeOffline', 'Bioscope is offline')
          : t('videoStream.startVideo', 'Start Video');

    return (
        <div className={styles.placeholderContainer}>
            <div className={styles.placeholderBackground} />
            <div className={styles.cageNameOverlay}>{cageName}</div>
            <button
                type="button"
                onClick={onStart}
                disabled={isDisabled}
                className={styles.playButton}
                title={buttonTitle}>
                <SvgIcon
                    icon={CirclePlay}
                    size="custom"
                    customSize={64}
                    className={styles.playIcon}
                />
            </button>
            {(controlState.isOutOfQuota || !controlState.isOnline) && (
                <div className={styles.statusMessage}>
                    {controlState.isOutOfQuota
                        ? t('videoStream.quotaExceeded', 'Daily streaming quota exceeded')
                        : t('videoStream.bioscopeOffline', 'Bioscope is offline')}
                </div>
            )}
        </div>
    );
}

interface LoadingViewProps {
    cageName?: string;
    onStop: () => void;
}

export function LoadingView(props: LoadingViewProps) {
    const { cageName, onStop } = props;
    const { t } = useTranslation('behaviorModule');

    return (
        <div className={styles.videoContainer}>
            <div className={styles.placeholderContainer}>
                <div className={styles.placeholderBackground} />
                <SvgIcon icon={VideoLoadingSpinner} size="custom" customSize={52} />
                <div className={styles.cageNameOverlay}>{cageName}</div>
                <div className={styles.loadingTextOverlay}>
                    {t('videoStream.loading', 'Connecting to live video. Please wait...')}
                </div>
            </div>
            <StopConnectionButton onStop={onStop} />
        </div>
    );
}

interface ErrorViewProps {
    cageName?: string;
    onRetry: () => void;
    onStop: () => void;
}

export function ErrorView(props: ErrorViewProps) {
    const { cageName, onRetry, onStop } = props;
    const { t } = useTranslation('behaviorModule');

    return (
        <div className={styles.videoContainer}>
            <div className={styles.placeholderContainer}>
                <div className={styles.placeholderBackground} />
                <button
                    type="button"
                    onClick={onRetry}
                    className={styles.playButton}
                    title={t('videoStream.retry', 'Retry')}>
                    <SvgIcon
                        icon={Reload}
                        color={settings.disabledColor}
                        size="custom"
                        customSize={52}
                    />
                </button>
                <div className={styles.cageNameOverlay}>{cageName}</div>
                <div className={styles.loadingTextOverlay}>
                    {t('videoStream.error', 'Error connecting to live video. Please retry.')}
                </div>
            </div>
            <StopConnectionButton onStop={onStop} />
        </div>
    );
}
